// src/pages/AdminDashboard/FeedbackManagement.js
import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { MdDelete, MdCheck, MdRefresh, MdSearch, MdBugReport, MdLightbulb, MdTrendingUp, MdFeedback, MdAccessTime } from 'react-icons/md';
import './FeedbackManagement.css';

export const FeedbackManagement = () => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchFeedback();
    }, []);

    const fetchFeedback = async () => {
        try {
            const response = await api.get('/admin/feedback');
            setFeedback(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch feedback');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleResolve = async (id) => {
        try {
            await api.put(`/admin/feedback/${id}/resolve`);
            setFeedback(feedback.map(item =>
                item.id === id ? { ...item, resolved: true } : item
            ));
        } catch (err) {
            setError('Failed to resolve feedback');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this feedback?')) {
            try {
                await api.delete(`/admin/feedback/${id}`);
                setFeedback(feedback.filter(item => item.id !== id));
            } catch (err) {
                setError('Failed to delete feedback');
            }
        }
    };

    const filteredFeedback = feedback
        .filter(item => {
            if (filter === 'all') return true;
            if (filter === 'pending') return !item.resolved;
            if (filter === 'resolved') return item.resolved;
            return true;
        })
        .filter(item =>
            item.feedback.toLowerCase().includes(search.toLowerCase()) ||
            item.feedbackType.toLowerCase().includes(search.toLowerCase())
        );

    return (
        <div className="admin-feedback">
            <header className="admin-feedback-header">
                <h1>Feedback Management</h1>
                <div className="admin-feedback-controls">
                    <div className="search-bar">
                        <MdSearch />
                        <input
                            type="text"
                            placeholder="Search feedback..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Feedback</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <button onClick={fetchFeedback} className="refresh-button">
                        <MdRefresh /> Refresh
                    </button>
                </div>
            </header>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading">Loading feedback...</div>
            ) : filteredFeedback.length === 0 ? (
                <div className="empty-state">No feedback found</div>
            ) : (
                <div className="feedback-grid">
                    {filteredFeedback.map(item => (
                        <div key={item.id} className={`feedback-card ${item.resolved ? 'resolved' : ''}`}>
                            <div className="feedback-header">
                                <span className={`feedback-type ${item.feedbackType || 'general'}`}>
                                    {item.feedbackType === 'bug' && <MdBugReport />}
                                    {item.feedbackType === 'feature' && <MdLightbulb />}
                                    {item.feedbackType === 'improvement' && <MdTrendingUp />}
                                    {item.feedbackType === 'general' && <MdFeedback />}
                                    {(item.feedbackType || 'general').charAt(0).toUpperCase() + (item.feedbackType || 'general').slice(1)}
                                </span>

                                <span className="feedback-date">
                                    <MdAccessTime />
                                    {new Date(item.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <p className="feedback-content">{item.feedback}</p>
                            <div className="feedback-footer">
                                <div className="feedback-user">
                                    From: {item.user_email || 'Anonymous'}
                                </div>
                                <div className="feedback-actions">
                                    {!item.resolved && (
                                        <button
                                            onClick={() => handleResolve(item.id)}
                                            className="resolve-button"
                                            title="Mark as resolved"
                                        >
                                            <MdCheck />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(item.id)}
                                        className="delete-button"
                                        title="Delete feedback"
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};